/*
 * DESIGN: Forge & Circuit — Neo-Brutalist Dark Tech
 * About: Split layout with cityscape background. Founder profile card with thick border.
 * Monospace data points. Industrial feel.
 */
import { MapPin, Globe, Award, Target } from "lucide-react";
import Img from "./Img";
import Reveal from "./Reveal";

const ABOUT_BG = "/images/about-section-TRETT2X7MvHNzaBrMTfHo6.webp";
const FOUNDER_IMG = "/images/founder-portrait-chris-peer.webp";

const values = [
  {
    icon: Target,
    title: "Built for the Trades",
    description: "We don't build generic AI. Every solution is purpose-built for the unique challenges of blue collar businesses.",
  },
  {
    icon: Award,
    title: "Results Over Hype",
    description: "No buzzwords, no fluff. We measure success by your bottom line — more calls answered, faster dispatch, lower costs.",
  },
  {
    icon: Globe,
    title: "Local Roots, Global Reach",
    description: "Headquartered in Columbus, OH, serving blue collar businesses worldwide. We understand your market because we're in it.",
  },
];

export default function About() {
  return (
    <section id="about" className="relative">
      {/* Background Image Section */}
      <div className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <Img
            src={ABOUT_BG}
            width={1920}
            height={1072}
            sizes="100vw"
            alt="City skyline at dusk"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-navy-deep/90" />
        </div>

        <div className="container relative z-10">
          {/* Section Header */}
          <div className="mb-16">
            <Reveal
              as="span"
              from="none"
              className="font-mono text-xs text-ember tracking-[0.3em] uppercase block mb-4"
            >
              // WHO WE ARE
            </Reveal>
            <Reveal
              as="h2"
              from="up"
              duration={0.6}
              className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4"
            >
              The Company Behind
              <br />
              <span className="text-electric">the Revolution</span>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Founder Card */}
            <Reveal
              from="left"
              duration={0.6}
              className="lg:col-span-2"
            >
              <div className="border-[3px] border-electric/30 bg-card overflow-hidden">
                <div className="aspect-[4/5] overflow-hidden">
                  <Img
                    src={FOUNDER_IMG}
                    width={1200}
                    height={1500}
                    sizes="(min-width: 1024px) 40vw, 100vw"
                    alt="Christopher D. Peer — Founder & Lead AI Architect"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-xl text-white mb-1">
                    Christopher D. Peer
                  </h3>
                  <p className="font-mono text-xs text-electric tracking-wider uppercase mb-4">
                    Founder & Lead AI Architect
                  </p>
                  <div className="flex items-center gap-2 text-steel-light text-sm">
                    <MapPin className="w-4 h-4 text-ember" />
                    Columbus, OH
                  </div>
                  <div className="flex items-center gap-2 text-steel-light text-sm mt-2">
                    <Globe className="w-4 h-4 text-ember" />
                    Serving World Wide
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Values & Mission */}
            <Reveal
              from="right"
              duration={0.6}
              delay={0.15}
              className="lg:col-span-3 flex flex-col justify-center"
            >
              <p className="text-steel-light text-lg leading-relaxed mb-8">
                Blue Collar AI was founded on a simple belief:{" "}
                <span className="text-white font-semibold">
                  the businesses that build, fix, and move America deserve the same
                  technology advantages as Fortune 500 companies.
                </span>
              </p>
              <p className="text-steel-light text-base leading-relaxed mb-10">
                Too many AI companies build for Silicon Valley. We build for Main Street.
                For the towing operator dispatching trucks at 2 AM. For the mechanic
                juggling 30 repair orders. For the small business owner who knows there's
                a better way but doesn't have time to figure it out. That's where we come in.
              </p>

              {/* Values Grid */}
              <div className="flex flex-col gap-5">
                {values.map((val, i) => (
                  <div
                    key={i}
                    className="flex gap-4 p-5 border-[2px] border-border bg-navy/40 hover:border-electric/30 transition-colors"
                  >
                    <div className="w-10 h-10 border-[2px] border-electric/30 bg-electric/10 flex items-center justify-center shrink-0">
                      <val.icon className="w-5 h-5 text-electric" />
                    </div>
                    <div>
                      <h4 className="font-heading font-bold text-white text-base mb-1">
                        {val.title}
                      </h4>
                      <p className="text-steel-light text-sm leading-relaxed">
                        {val.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
